import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { SelectField } from '@aquilixshop/ui-react';
import { FieldRow } from '../FieldRow';
import { useAnchoredMenu } from './anchoredMenu';
import { bt } from '../../i18n';

/**
 * Select (Shopify-parity): ALWAYS a compact dropdown (a custom in-app listbox — never a native
 * `<select>`, per the no-native-popup spirit), label left / control right. Short 2-option choices that
 * should read as a segmented toggle use the dedicated `radio` field type instead. A stale stored value
 * (not in options) falls back to the field default for display. Commits immediately.
 */
export function SelectControl({
  field,
  value,
  onChange,
}: {
  field: SelectField;
  value: string | undefined;
  onChange: (next: string) => void;
}) {
  const known = field.options.some((o) => o.value === value);
  const current = known ? value : (field.default ?? field.options[0]?.value);

  return (
    <FieldRow id={field.id} label={field.label} info={field.info} labelAs="span" layout="row">
      <Listbox field={field} current={current} onChange={onChange} />
    </FieldRow>
  );
}

function Listbox({
  field,
  current,
  onChange,
}: {
  field: SelectField;
  current: string | undefined;
  onChange: (next: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);
  const currentLabel =
    field.options.find((o) => o.value === current)?.label ?? bt('chrome.common.select', 'Select…');
  // The menu is PORTALLED to <body> (the inspector clips `overflow: hidden`), so it is positioned in
  // viewport coordinates and flips above the trigger when the field sits near the bottom of the panel.
  const pos = useAnchoredMenu(open, btnRef, menuRef);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      // The menu no longer lives inside `ref` — an outside-click test that ignored it would close the
      // list on mousedown, before the option's click ever fired.
      if (ref.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  return (
    <div className="bld-listbox" ref={ref}>
      <button
        ref={btnRef}
        type="button"
        className="bld-listbox__btn"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setOpen(false);
        }}
      >
        <span className="bld-listbox__val">{currentLabel}</span>
        <svg
          className="bld-listbox__caret"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M5 6.5L8 3.5L11 6.5M5 9.5L8 12.5L11 9.5"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {open &&
        createPortal(
          <ul
            ref={menuRef}
            className="bld-listbox__menu bld-listbox__menu--portal"
            role="listbox"
            aria-label={field.label}
            style={{
              top: pos.top,
              left: pos.left,
              width: pos.width || undefined,
              maxHeight: pos.maxHeight,
            }}
          >
            {field.options.map((o) => (
              // A `disabled` option (a capability the STORE isn't set up for — e.g. card payments with no
              // connected Stripe) stays VISIBLE but inert: greyed, aria-disabled, click ignored. Hiding it
              // would leave the seller wondering where the feature went; letting them pick it would let the
              // builder promise a checkout the storefront can't deliver.
              <li
                key={o.value}
                role="option"
                aria-selected={o.value === current}
                aria-disabled={o.disabled || undefined}
                className={`bld-listbox__opt${o.disabled ? ' bld-listbox__opt--disabled' : ''}`}
                onClick={() => {
                  if (o.disabled) return;
                  onChange(o.value);
                  setOpen(false);
                }}
              >
                {o.label}
              </li>
            ))}
          </ul>,
          document.body,
        )}
    </div>
  );
}
